import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { TiTickOutline } from 'react-icons/ti';
import { Link } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import { useAuth, useAutoSave, useForm, useLoadSection, useRouter } from '~/hooks';
import { ROLES } from '~/util/auth';
import { CheckBox, FormSelect } from '~/components/form';
import { Review as DeedReview } from '../File';
import { Review as DeedOfTrustReview } from '../DeedOfTust';
import { Review as AdditionalReview } from '../Additional';
import { lawfirm } from '~/api';
import sectionTypes from '../sectionTypes';

const initialState = {
    sectionId: 0,
    orderNumber: null,
    sectionErrors: {
        10: false,
        11: false,
        12: false
    },
    firmUsers: [],
    attorneyUser: false,
    attorney: null,
    approvingAttorney: null,
};

const load = async (values, { user }) => {
    const firmUsers = (await lawfirm.getUsersForFirm(user.firmId))
        .filter(x => x.roleId == ROLES.FIRM_ADMIN)
        .map(f => ({
            label: `${f.firstName} ${f.lastName}`,
            value: f.id
        }));
    
    return {
        ...values,
        firmUsers
    };
};

const Review = ({ titleOpinionId, readonly, hasFileNumber }) => {
    
    const [isLoading, setIsLoading] = useState(false);

    const _readonly = useMemo(() => readonly || !hasFileNumber, [readonly, hasFileNumber]);

    const { pathname } = useRouter();

    const { user }  = useAuth();

    const { onLoad: loadSection } = useLoadSection(sectionTypes.REVIEW, titleOpinionId);

    const onLoad = useCallback((values, { user }) => {
        setIsLoading(true);
        return load(values, { user }).then(v => {
            return loadSection(v).then((v)=>{
                setIsLoading(false);
                return v;
            });
        });
    }, [titleOpinionId]);

    const {
        state: { values },
        fieldActions
    } = useForm({ 
        initialState, 
        props: { user },
        onLoad 
    });

    const { saveField } = useAutoSave(sectionTypes.REVIEW, titleOpinionId, values.sectionId);

    const handleValidation = (sectionType, hasErrors) => {
        
        let errors = {
            ...values.sectionErrors,
            [sectionType]: hasErrors
        };
        
        fieldActions.onChange(hasErrors, `sectionErrors.${sectionType}`);

        if (values.sectionId !== 0) {
            saveField(errors, 'sectionErrors', values.sectionId, null);
        }
    };

    const saveOnChange = useCallback(async (value, path) => {
        let saveData = {
            approvingAttorney: values.approvingAttorney,
            attorney: values.attorney,
            attorneyUser: values.attorneyUser,
            sectionErrors: values.sectionErrors,
            orderNumber: values.orderNumber
        };

        saveData[path] = value;
        
        if (saveData.attorneyUser) {
            saveData.approvingAttorney = user.id;
        } else if (saveData.attorney?.value > 0) {
            saveData.approvingAttorney = saveData.attorney.value;
        } else {
            delete saveData.approvingAttorney;
        }

        await saveField(value, path, values.sectionId, saveData);
        return fieldActions.onChange(value, path);
    }, [saveField]);

    const setApprovingAttorney = async (value, path) => {
        let attorney = null;
        if (path == 'attorneyUser' && value) {
            attorney = values.firmUsers
                .find(u => u.value == user.id);
            fieldActions.onChange(value, 'attorneyUser');
        } else if (path == 'attorney' && !isEmpty(value)) {
            attorney = values.firmUsers
                .find(u => u.value == value.value);
        } 

        saveOnChange(value, path);

        return fieldActions.onChange(attorney.value, 'approvingAttorney');
    };

    useEffect(() => {
        if (values.sectionId)
            saveOnChange(values.sectionErrors, 'sectionErrors');
    }, [values.sectionErrors]);

    return (
        <div className="mx-5">
            <div className="mb-5">
                <div>
                    <div className="z-10 flex items-center h-full text-xl font-semibold text-blue-60">
                        <div className="h-11 w-11 rounded-md flex items-center justify-center bg-blue-30"><TiTickOutline size="45%"/></div>
                        <h2 className="ml-2"> Final Title Opinion Review </h2>
                    </div> 
                </div>
            </div>
            <span className="block text-sm text-neutral-100 font-semibold  w-3/4 mb-2">Please review and confirm the final title opinion information you have provided. Any incompleted information has been indentified and must be completed before submitting.</span>
            <div className='border-b border-gray-290 my-4 py-2'/>
            {isLoading === false &&
            <>
                <Link 
                    to={pathname.replace(/\/review$/i, '/file')}
                    className='text-primary-60 font-semibold'
                >
                    1. Deed Information
                </Link>
                <DeedReview titleOpinionId={titleOpinionId} handleValidation={(result) => handleValidation(sectionTypes.FILE, result)} />
                <div className='border-b border-gray-290 mb-4 py-2'/>
                <Link 
                    to={pathname.replace(/\/review$/i, '/deed')}
                    className='text-primary-60 font-semibold mt-2'
                >
                    2. Deed of Trust
                </Link>
                <DeedOfTrustReview titleOpinionId={titleOpinionId} handleValidation={result => handleValidation(sectionTypes.DEED, result)}/>
                <div className='border-b border-gray-290 mb-4 py-2'/>
                <Link 
                    to={pathname.replace(/\/review$/i, '/additional')}
                    className='text-primary-60 font-semibold mb-4'
                >
                    3. Additional Information
                </Link>
                <AdditionalReview titleOpinionId={titleOpinionId} handleValidation={result => handleValidation(sectionTypes.ADDITIONAL, result)}/>
                <div className='border-b border-gray-290 mb-4 py-2'/>
                <p className='text-neutral-100 font-semibold text-sm'>
                    This Opinion of title is for the parties to whom it is furnished, is not transferable, and may not be used by any other person or entity without the prior written consent of the undersigned.
                </p>
                { user.roleId == ROLES.FIRM_ADMIN
                    ? <CheckBox
                        label={user.name}
                        readOnly={_readonly}
                        path='attorneyUser'
                        value={values.attorneyUser}
                        groupClassName='text-sm flex items-center py-2 my-4'
                        inputClassName='mr-2 border-1 border-gray-200 rounded bg-gray-50'
                        onChange={setApprovingAttorney}
                    />
                    : <FormSelect
                        type='select'
                        required
                        readOnly={_readonly}
                        groupClassName="w-1/4 flex items-center text-xs my-6"
                        className="w-full border-1 border-gray-200 rounded bg-gray-50"
                        options={values.firmUsers}
                        path='attorney'
                        value={values.attorney}
                        onChange={(attorney, path) => setApprovingAttorney(attorney, path)}
                    />
                }
            </>
            }
            <p className='text-neutral-100 font-semibold text-xs my-4'>
                By selecting the &quot;Submit&quot; button, you are signing this &quot;document&quot; electronically. You agree your electronic signature is the legal equivalent of your manual signature on this document. By selecting &quot;Submit&quot;, you consent to be legally bound by this document&apos;s terms and conditions.
            </p>
        </div>
    );
};

export default Review;